/**
 * Parallax Layer Component
 * Creates smooth parallax scrolling effect with optional zoom for background layers
 */

const parallaxLayerInit = () => {
    Alpine.data('parallaxLayer', (speed = 0.5, zoom = '0') => ({
        speed: parseFloat(speed) || 0.5,
        zoomDirection: 0, // 1 = zoom in, -1 = zoom out, 0 = none
        zoomIntensity: 0,
        translateY: 0,
        scale: 1,
        observer: null,
        isVisible: false,
        rafId: null,

        init() {
            this.parseZoom(zoom)
            this.setupObserver()
            this.bindScroll()
        },

        parseZoom(zoomValue) {
            if (!zoomValue || zoomValue === '0') {
                this.zoomDirection = 0
                this.zoomIntensity = 0
                return
            }

            const parts = zoomValue.split('_')
            if (parts.length === 2) {
                this.zoomDirection = parts[0] === 'in' ? 1 : -1
                this.zoomIntensity = parseFloat(parts[1]) || 0
            }
        },

        destroy() {
            if (this.observer) {
                this.observer.disconnect()
            }
            if (this.rafId) {
                cancelAnimationFrame(this.rafId)
            }
            window.removeEventListener('scroll', this.onScroll)
        },

        setupObserver() {
            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        this.isVisible = entry.isIntersecting
                        if (this.isVisible) {
                            this.updatePosition()
                        }
                    })
                },
                { threshold: 0, rootMargin: '100px' }
            )
            this.observer.observe(this.$el.parentElement || this.$el)
        },

        bindScroll() {
            this.onScroll = () => {
                if (!this.isVisible) return
                if (this.rafId) return

                this.rafId = requestAnimationFrame(() => {
                    this.updatePosition()
                    this.rafId = null
                })
            }
            window.addEventListener('scroll', this.onScroll, { passive: true })
        },

        updatePosition() {
            const parent = this.$el.parentElement || this.$el
            const rect = parent.getBoundingClientRect()
            const windowHeight = window.innerHeight

            // Calculate how far the element is from center of viewport
            const elementCenter = rect.top + rect.height / 2
            const viewportCenter = windowHeight / 2
            const distanceFromCenter = elementCenter - viewportCenter

            // Apply parallax offset based on speed
            // Speed 0 = no movement, Speed 1 = moves with scroll
            this.translateY = distanceFromCenter * this.speed * -1

            // Calculate zoom based on scroll position
            // Progress: 0 = element entering viewport, 1 = element leaving viewport
            if (this.zoomIntensity > 0) {
                const totalScrollDistance = windowHeight + rect.height
                const scrolledDistance = windowHeight - rect.top
                const progress = Math.max(0, Math.min(1, scrolledDistance / totalScrollDistance))

                // Calculate scale based on direction and progress
                // Zoom in: starts at 1, grows as user scrolls down
                // Zoom out: starts larger, shrinks as user scrolls down
                if (this.zoomDirection === 1) {
                    // Zoom in: scale increases as element moves up (scrolling down)
                    this.scale = 1 + (progress * this.zoomIntensity)
                } else if (this.zoomDirection === -1) {
                    // Zoom out: scale decreases as element moves up (scrolling down)
                    this.scale = 1 + this.zoomIntensity - (progress * this.zoomIntensity)
                }
            } else {
                this.scale = 1
            }

            this.$el.style.transform = `translate3d(0, ${this.translateY}px, 0) scale(${this.scale})`
        },
    }))
}

document.addEventListener('alpine:init', () => {
    parallaxLayerInit()
})
